import { motion } from 'framer-motion';
import { Leaf, Award, Users, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const stats = [
  { icon: Clock, value: '20+', label: "Années d'expérience" },
  { icon: Users, value: '193', label: 'Avis clients' },
  { icon: Award, value: '4.9', label: 'Note Google' },
  { icon: Leaf, value: '100%', label: 'Fleurs fraîches' },
];

export const AboutSection = () => {
  return (
    <section
      data-testid="about-section"
      className="py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
                  alt="Intérieur de la boutique Alma Fleur"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
              </div>
              
              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute -bottom-8 -right-4 md:-right-8 glass rounded-2xl p-6 shadow-xl max-w-xs"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-accent" />
                  </div>
                  <span className="font-serif text-lg font-semibold text-primary">
                    Savoir-faire artisanal
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Chaque création est réalisée à la main avec passion et minutie.
                </p>
              </motion.div>

              {/* Decorative Element */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-accent/10 rounded-full blur-2xl" />
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <span className="text-accent font-accent italic text-lg">
                Notre histoire
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-primary tracking-tight mt-2">
                Une passion pour
                <br />
                <span className="text-accent">l'art floral</span>
              </h2>
            </div>

            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Située au cœur de Sion, <strong className="text-primary">Alma Fleur SA</strong> est 
                bien plus qu'une simple fleuristerie. C'est un lieu où la beauté de la nature 
                rencontre le savoir-faire artisanal suisse.
              </p>
              <p>
                Notre équipe passionnée sélectionne chaque fleur avec soin, privilégiant la 
                fraîcheur et la qualité pour créer des compositions qui émerveillent et 
                transmettent vos émotions les plus sincères.
              </p>
              <p>
                Que ce soit pour célébrer un moment de joie, exprimer votre amour ou 
                accompagner un instant de recueillement, nous mettons notre expertise 
                à votre service.
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-border">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary mx-auto mb-2 flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="font-serif text-2xl font-semibold text-primary">
                    {stat.value}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <Link to="/a-propos" data-testid="about-more-btn">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg font-medium border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all mt-4"
              >
                En savoir plus
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
